import React, { useState, useRef } from 'react';

const QuestionReviewEditor = ({ 
  questions = [], 
  onQuestionsUpdate, 
  onConfirm, 
  onCancel,
  isLoading = false 
}) => {
  const [editingQuestions, setEditingQuestions] = useState(questions);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const dragCounter = useRef(0);

  // 문항 업데이트
  const updateQuestion = (questionIndex, field, value) => {
    const updated = [...editingQuestions];
    updated[questionIndex] = {
      ...updated[questionIndex],
      [field]: value
    };
    setEditingQuestions(updated);
    onQuestionsUpdate?.(updated);
  };

  // 정답 체크박스 토글
  const toggleCorrectAnswer = (questionIndex, choiceIndex) => {
    const updated = [...editingQuestions];
    updated[questionIndex].correctIndex = choiceIndex;
    updated[questionIndex].answer = updated[questionIndex].choices[choiceIndex] || '';
    setEditingQuestions(updated);
    onQuestionsUpdate?.(updated);
  };

  // 선택지 업데이트
  const updateChoice = (questionIndex, choiceIndex, value) => {
    const updated = [...editingQuestions];
    updated[questionIndex].choices[choiceIndex] = value;
    
    // 만약 이 선택지가 현재 정답이라면, answer 필드도 업데이트
    if (updated[questionIndex].correctIndex === choiceIndex) {
      updated[questionIndex].answer = value;
    }
    
    setEditingQuestions(updated);
    onQuestionsUpdate?.(updated);
  };

  // 선택지 삭제
  const removeChoice = (questionIndex, choiceIndex) => {
    const updated = [...editingQuestions];
    updated[questionIndex].choices.splice(choiceIndex, 1);
    
    // 정답 인덱스 조정
    if (updated[questionIndex].correctIndex === choiceIndex) {
      // 삭제된 선택지가 정답이었다면, 첫 번째 선택지를 정답으로 설정
      updated[questionIndex].correctIndex = 0;
      updated[questionIndex].answer = updated[questionIndex].choices[0] || '';
    } else if (updated[questionIndex].correctIndex > choiceIndex) {
      // 정답 인덱스가 삭제된 선택지보다 뒤에 있다면, 인덱스를 1 감소
      updated[questionIndex].correctIndex--;
    }
    
    setEditingQuestions(updated);
    onQuestionsUpdate?.(updated);
  };

  // 선택지 추가
  const addChoice = (questionIndex) => {
    const updated = [...editingQuestions];
    updated[questionIndex].choices.push('');
    setEditingQuestions(updated);
    onQuestionsUpdate?.(updated);
  };

  // 문항 삭제
  const removeQuestion = (questionIndex) => {
    const updated = editingQuestions.filter((_, index) => index !== questionIndex);
    setEditingQuestions(updated);
    onQuestionsUpdate?.(updated);
  };

  // 새 문항 추가
  const addQuestion = () => {
    const newQuestion = {
      question: '',
      choices: ['', '', '', ''],
      answer: '',
      correctIndex: 0,
      difficulty: 'Medium',
      explanation: ''
    };
    const updated = [...editingQuestions, newQuestion];
    setEditingQuestions(updated);
    onQuestionsUpdate?.(updated);
  };

  // 드래그 앤 드롭 핸들러
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    dragCounter.current++;
  };

  const handleDragLeave = (e) => {
    dragCounter.current--;
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    dragCounter.current = 0;
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const updated = [...editingQuestions];
    const draggedItem = updated[draggedIndex];
    updated.splice(draggedIndex, 1);
    updated.splice(dropIndex, 0, draggedItem);
    
    setEditingQuestions(updated);
    onQuestionsUpdate?.(updated);
    setDraggedIndex(null);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-white">
          <h2 className="text-xl font-semibold text-gray-800">
            문항 검토 및 수정
          </h2>
          <button 
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isLoading}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {editingQuestions.map((question, questionIndex) => (
              <div
                key={questionIndex}
                draggable
                onDragStart={(e) => handleDragStart(e, questionIndex)}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, questionIndex)}
                className={`bg-white border-2 border-gray-200 rounded-lg p-6 transition-all duration-200 hover:border-blue-300 hover:shadow-md ${
                  draggedIndex === questionIndex ? 'opacity-50 scale-95' : ''
                }`}
                style={{ cursor: 'grab' }}
                onMouseDown={(e) => {
                  // 입력 필드나 버튼이 아닌 영역에서만 드래그 커서 표시
                  const target = e.target;
                  if (target.tagName !== 'INPUT' && target.tagName !== 'BUTTON' && target.tagName !== 'SVG' && target.tagName !== 'PATH') {
                    e.currentTarget.style.cursor = 'grabbing';
                  }
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.cursor = 'grab';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.cursor = 'grab';
                }}
              >
                {/* 문항 헤더 */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3 flex-1">
                    {/* 드래그 핸들 */}
                    <div className="flex items-center space-x-2 cursor-grab hover:cursor-grabbing">
                      <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"></path>
                      </svg>
                      <span className="text-lg font-bold text-red-500 min-w-0">
                        {questionIndex + 1}.
                      </span>
                    </div>
                    <input
                      type="text"
                      value={question.question}
                      onChange={(e) => updateQuestion(questionIndex, 'question', e.target.value)}
                      placeholder="문항을 입력하세요..."
                      className="flex-1 text-lg font-medium border-none outline-none focus:bg-gray-50 rounded px-2 py-1 cursor-text"
                    />
                  </div>
                  <button
                    onClick={() => removeQuestion(questionIndex)}
                    className="text-red-400 hover:text-red-600 transition-colors ml-2 p-1"
                    title="문항 삭제"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>

                {/* 정답 정보 표시 */}
                <div className="mb-4">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700">
                      정답 선택
                    </label>
                    <span className="text-xs text-gray-500">
                      현재 정답: {question.correctIndex !== undefined ? 
                        `${question.correctIndex + 1}번 (${question.choices?.[question.correctIndex] || '미입력'})` : 
                        '선택되지 않음'}
                    </span>
                  </div>
                </div>

                {/* 선택지 목록 */}
                <div className="space-y-2">
                  {question.choices?.map((choice, choiceIndex) => (
                    <div key={choiceIndex} className="flex items-center space-x-3 group">
                      {/* 정답 체크박스 */}
                      <div className="flex items-center">
                        <input
                          type="radio"
                          id={`correct-${questionIndex}-${choiceIndex}`}
                          name={`correct-${questionIndex}`}
                          checked={question.correctIndex === choiceIndex}
                          onChange={() => toggleCorrectAnswer(questionIndex, choiceIndex)}
                          className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 focus:ring-green-500 focus:ring-2"
                        />
                        <label 
                          htmlFor={`correct-${questionIndex}-${choiceIndex}`}
                          className={`ml-1 text-xs cursor-pointer transition-colors ${
                            question.correctIndex === choiceIndex 
                              ? 'text-green-600' 
                              : 'text-gray-300 hover:text-gray-400'
                          }`}
                          title="정답으로 선택"
                        >
                          {question.correctIndex === choiceIndex ? '✓' : '○'}
                        </label>
                      </div>
                      
                      {/* 선택지 번호 */}
                      <div className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-medium transition-colors ${
                        question.correctIndex === choiceIndex 
                          ? 'bg-green-100 text-green-700 border-2 border-green-300' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {choiceIndex + 1}
                      </div>
                      
                      {/* 선택지 입력 */}
                      <input
                        type="text"
                        value={choice}
                        onChange={(e) => updateChoice(questionIndex, choiceIndex, e.target.value)}
                        placeholder={`선택지 ${choiceIndex + 1}`}
                        className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent transition-all ${
                          question.correctIndex === choiceIndex
                            ? 'border-green-300 focus:ring-green-500 bg-green-50'
                            : 'border-gray-300 focus:ring-blue-500'
                        }`}
                      />
                      
                      {/* 삭제 버튼 */}
                      <button
                        onClick={() => removeChoice(questionIndex, choiceIndex)}
                        className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-all duration-200 p-1"
                        title="선택지 삭제"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                  
                  {/* 선택지 추가 버튼 */}
                  <button
                    onClick={() => addChoice(questionIndex)}
                    className="flex items-center space-x-2 text-blue-500 hover:text-blue-700 transition-colors mt-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    <span className="text-sm">선택지 추가</span>
                  </button>
                </div>

                {/* 해설 입력 */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    해설
                  </label>
                  <textarea
                    value={question.explanation || question.해설 || ''}
                    onChange={(e) => updateQuestion(questionIndex, 'explanation', e.target.value)}
                    placeholder="문항에 대한 해설을 입력하세요..."
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                  />
                </div>

                {/* 난이도 표시 */}
                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">난이도:</span>
                    <select
                      value={question.difficulty || 'Medium'}
                      onChange={(e) => updateQuestion(questionIndex, 'difficulty', e.target.value)}
                      className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                    </select>
                  </div>
                  <div className="text-xs text-gray-400">
                    드래그하여 순서 변경
                  </div>
                </div>
              </div>
            ))}

            {/* 문항 추가 버튼 */}
            <div className="text-center">
              <button
                onClick={addQuestion}
                className="inline-flex items-center space-x-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-300 hover:text-blue-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span>새 문항 추가</span>
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            총 {editingQuestions.length}개 문항
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              disabled={isLoading}
            >
              취소
            </button>
            
            {/* 완료 버튼 (배포/다운로드 모달 열기) */}
            <button
              onClick={() => onConfirm(editingQuestions)}
              disabled={isLoading || editingQuestions.length === 0}
              className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              {isLoading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              )}
              {isLoading ? 'HTML 생성 중...' : '완료'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionReviewEditor;